import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import routes from 'routes';
import * as Icon from 'react-feather';
import TableComponent from 'components/widgets/table';
import { Formik } from 'formik';
import * as yup from 'yup';

const Container = styled.div`
  width: 100%;
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
`;

const Title = styled.h2`
  margin: 0;
  color: #222;
`;

const BackButton = styled.button`
  background: transparent;
  border: 1px solid #ddd;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
`;

const Section = styled.div`
  margin-top: 18px;
  margin-bottom: 18px;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 220px;
  flex: 1 1 280px;
`;

const Label = styled.label`
  font-size: 13px;
  color: #444;
  margin-bottom: 6px;
  font-weight: 600;
`;

const Input = styled.input`
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
`;

const Select = styled.select`
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
`;

const ButtonPrimary = styled.button`
  background: #001f5f;
  color: #fff;
  border: none;
  padding: 8px 14px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ButtonGhost = styled.button`
  background: transparent;
  border: 1px solid #ddd;
  color: #333;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
`;

const Small = styled.div`
  font-size: 13px;
  color: #666;
`;

const CapabilitiesGrid = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const CapCard = styled.div`
  min-width: 160px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #f1f6ff;
  color: #001f5f;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;

export default function DMVICIntegration() {
  const history = useHistory();

  // credentials
  const [credentialsSaved, setCredentialsSaved] = useState(false);

  // add config
  const [configs, setConfigs] = useState([]);

  // sample underwriter options; replace with real API data
  const underwriterOptions = [
    { value: '', label: '-- select underwriter --' },
    { value: 'uw_abc', label: 'ABC Underwriters' },
    { value: 'uw_xyz', label: 'XYZ Insurance' },
    { value: 'uw_safe', label: 'SafeDrive Insurance' },
  ];

  // Yup schemas
  const CredSchema = yup.object().shape({
    username: yup.string().required('Username is required'),
    password: yup.string().required('Password is required'),
  });

  const ConfigSchema = yup.object().shape({
    configName: yup.string().required('Config name is required'),
    underwriter: yup.string().required('Underwriter is required'),
    memberId: yup.string().required('Member ID is required'),
  });

  const defaultCapabilities = ['Issue Certificate', 'Download Certificate', 'View Logs'];

  // derived table data for reusable table component
  const tableData = useMemo(() => {
    return configs.map((c) => ({
      ...c,
      underwriterLabel: underwriterOptions.find((u) => u.value === c.underwriter)?.label || c.underwriter,
      capabilitiesString: (c.capabilities || defaultCapabilities).join(', '),
    }));
  }, [configs]);

  // table configuration
  const tableFields = [
    { name: 'Config name' },
    { name: 'Underwriter' },
    { name: 'Member ID' },
    { name: 'Capabilities' },
    { name: 'Actions' },
  ];

  const rowData = [
    { type: 'text', field: 'configName' },
    { type: 'text', field: 'underwriterLabel' },
    { type: 'text', field: 'memberId' },
    { type: 'text', field: 'capabilitiesString' },
    {
      type: 'action',
      actions: [
        { method: (id) => configureConfig(id), field: 'id', title: 'Configure' },
        { method: (id) => editConfig(id), field: 'id', title: 'Edit' },
        { method: (id) => removeConfig(id), field: 'id', title: 'Remove' },
      ],
    },
  ];

  function saveCredentials(values, { setSubmitting, resetForm }) {
    // mock save
    setTimeout(() => {
      setCredentialsSaved(true);
      setSubmitting(false);
    }, 400);
  }

  function onAddConfig(values, { setSubmitting, resetForm }) {
    const newItem = {
      id: `${Date.now()}`,
      configName: values.configName.trim(),
      underwriter: values.underwriter,
      memberId: values.memberId.trim(),
      capabilities: defaultCapabilities,
    };
    setConfigs((p) => [newItem, ...p]);
    setSubmitting(false);
    resetForm();
  }

  function removeConfig(id) {
    setConfigs((p) => p.filter((c) => c.id !== id));
  }

  function editConfig(id) {
    const cfg = configs.find((c) => c.id === id);
    if (!cfg) return;
    // populate add form values by pre-filling a temporary form — we'll open a prompt for simplicity
    // For now, remove and allow user to re-add via form
    setConfigs((p) => p.filter((c) => c.id !== id));
    // scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // To make editing nicer we could open a modal or pre-fill a form; skipping for now
  }

  function configureConfig(id) {
    const cfg = configs.find((c) => c.id === id);
    // placeholder: open modal or navigate to a detailed config page; we'll alert for now
    // eslint-disable-next-line no-alert
    alert(`Configure: ${cfg?.configName || id}`);
  }

  return (
    <Container>
      <Header>
        <Title>DMVIC Integration</Title>
        <BackButton onClick={() => history.push(routes.integrationpanel)}>
          <Icon.ChevronLeft size={14} style={{ marginRight: 8 }} />
          Back
        </BackButton>
      </Header>

      {/* Credentials section with Formik + Yup */}
      <Section>
        <Small style={{ marginBottom: 8, fontWeight: 700 }}>Credentials</Small>
        <Formik
          initialValues={{ username: '', password: '' }}
          validationSchema={CredSchema}
          onSubmit={saveCredentials}
        >
          {({ values, errors, touched, handleChange, handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <Row>
                <FormGroup>
                  <Label>Username</Label>
                  <Input name="username" value={values.username} onChange={handleChange} placeholder="DMVIC username" />
                  {errors.username && touched.username && <div style={{ color: 'crimson', marginTop: 6 }}>{errors.username}</div>}
                </FormGroup>

                <FormGroup>
                  <Label>Password</Label>
                  <Input name="password" type="password" value={values.password} onChange={handleChange} placeholder="DMVIC password" />
                  {errors.password && touched.password && <div style={{ color: 'crimson', marginTop: 6 }}>{errors.password}</div>}
                </FormGroup>

                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <ButtonPrimary type="submit" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save Credentials'}</ButtonPrimary>
                </div>
              </Row>
              {credentialsSaved && <div style={{ marginTop: 8, color: 'green' }}>Credentials saved (mock)</div>}
            </form>
          )}
        </Formik>
      </Section>

      {/* Capabilities overview (simple cards) */}
      <Section>
        <Small style={{ marginBottom: 8, fontWeight: 700 }}>Capabilities</Small>
        <CapabilitiesGrid>
          {defaultCapabilities.map((cap) => (
            <CapCard key={cap}>
              <Icon.CheckCircle />
              <div style={{ fontWeight: 700 }}>{cap}</div>
            </CapCard>
          ))}
        </CapabilitiesGrid>
      </Section>

      {/* Add config form (Formik + Yup) */}
      <Section>
        <Small style={{ marginBottom: 8, fontWeight: 700 }}>Add DMVIC Config</Small>
        <Formik
          initialValues={{ configName: '', underwriter: '', memberId: '' }}
          validationSchema={ConfigSchema}
          onSubmit={onAddConfig}
        >
          {({ values, errors, touched, handleChange, handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <Row>
                <FormGroup>
                  <Label>Config name</Label>
                  <Input name="configName" value={values.configName} onChange={handleChange} placeholder="e.g. Default DMVIC" />
                  {errors.configName && touched.configName && <div style={{ color: 'crimson', marginTop: 6 }}>{errors.configName}</div>}
                </FormGroup>

                <FormGroup>
                  <Label>Underwriter</Label>
                  <Select name="underwriter" value={values.underwriter} onChange={handleChange}>
                    {underwriterOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                  {errors.underwriter && touched.underwriter && <div style={{ color: 'crimson', marginTop: 6 }}>{errors.underwriter}</div>}
                </FormGroup>

                <FormGroup>
                  <Label>Member ID</Label>
                  <Input name="memberId" value={values.memberId} onChange={handleChange} placeholder="Member ID" />
                  {errors.memberId && touched.memberId && <div style={{ color: 'crimson', marginTop: 6 }}>{errors.memberId}</div>}
                </FormGroup>

                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <ButtonPrimary type="submit" disabled={isSubmitting}>{isSubmitting ? 'Adding...' : 'Add'}</ButtonPrimary>
                </div>
              </Row>
            </form>
          )}
        </Formik>
      </Section>

      {/* Configs table using reusable TableComponent */}
      <Section>
        <Small style={{ marginBottom: 8, fontWeight: 700 }}>Configured DMVIC Integrations</Small>
        <TableComponent
          title="DMVIC Configurations"
          table_fields={tableFields}
          table_data={tableData}
          row_data={rowData}
          pageSize={10}
        />
      </Section>
    </Container>
  );
}
